/**
 * Talking to the API.
 *
 * The access token is held **in a module variable, not in localStorage**. It
 * lives fifteen minutes and never survives a reload on purpose: what survives
 * is the refresh cookie, which script on this page cannot read. Putting the
 * access token in localStorage would hand it to any script that ever manages
 * to run here, in exchange for saving one request at startup.
 *
 * That startup request is `refresh()`: the app opens, asks whether the cookie
 * it cannot see is still good, and either gets a session back or shows the
 * sign-in screen.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: Array<{ path: string; message: string }> = [],
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** The message for a field, if the server rejected that field by name. */
  issueFor(path: string): string | undefined {
    return this.issues.find((issue) => issue.path === path)?.message
  }
}

let accessToken: string | null = null
let onSessionLost: (() => void) | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

/** Told to the session provider so a lost session can move the app to sign-in. */
export function setSessionLostHandler(handler: () => void): void {
  onSessionLost = handler
}

/**
 * One refresh at a time.
 *
 * A screen that fires three requests at once gets three 401s at once when the
 * token expires. Without this they would each start a refresh, and because
 * refresh *rotates* the token, the second would present one the first had
 * already spent — which the server correctly treats as a stolen token and
 * answers by ending every session the user has. Sharing one promise is what
 * stops the client mugging itself.
 */
let inFlightRefresh: Promise<boolean> | null = null

async function refreshOnce(): Promise<boolean> {
  inFlightRefresh ??= (async () => {
    try {
      const response = await fetch('/api/auth/refresh', {
        method: 'POST',
        credentials: 'same-origin',
      })
      if (!response.ok) return false
      const body = (await response.json()) as { accessToken: string }
      accessToken = body.accessToken
      return true
    } catch {
      return false
    } finally {
      // Cleared on the next tick so everyone waiting on this attempt sees the
      // same answer before a new one can start.
      queueMicrotask(() => {
        inFlightRefresh = null
      })
    }
  })()

  return inFlightRefresh
}

interface RequestOptions {
  method?: string
  body?: unknown
  /** Set for the calls that must not trigger the sign-in redirect. */
  anonymous?: boolean
}

async function request<T>(path: string, options: RequestOptions = {}, retry = true): Promise<T> {
  const isFormData = options.body instanceof FormData
  const headers: Record<string, string> = {}
  // A FormData body gets no content-type header at all: the browser sets its
  // own, boundary included, and overriding it here would break the parse.
  if (options.body !== undefined && !isFormData) headers['content-type'] = 'application/json'
  if (accessToken && !options.anonymous) headers['authorization'] = `Bearer ${accessToken}`

  const response = await fetch(`/api${path}`, {
    method: options.method ?? 'GET',
    headers,
    // Same origin in both development and production — the dev server proxies
    // /api to Fastify precisely so this stays true.
    credentials: 'same-origin',
    body:
      options.body === undefined
        ? undefined
        : options.body instanceof FormData
          ? options.body
          : JSON.stringify(options.body),
  })

  if (response.status === 401 && retry && !options.anonymous) {
    if (await refreshOnce()) return request<T>(path, options, false)
    accessToken = null
    onSessionLost?.()
  }

  if (response.status === 204) return undefined as T

  const text = await response.text()
  const body: unknown = text ? JSON.parse(text) : undefined

  if (!response.ok) {
    const problem = (body ?? {}) as {
      error?: string
      message?: string
      issues?: Array<{ path: string; message: string }>
    }
    throw new ApiError(
      response.status,
      problem.error ?? 'ERROR',
      problem.message ?? 'Something went wrong',
      problem.issues ?? [],
    )
  }

  return body as T
}

export const api = {
  get: <T,>(path: string) => request<T>(path),
  post: <T,>(path: string, body?: unknown, options: { anonymous?: boolean } = {}) =>
    request<T>(path, { method: 'POST', body, ...options }),
  put: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PUT', body }),
  patch: <T,>(path: string, body?: unknown) => request<T>(path, { method: 'PATCH', body }),
  upload: <T,>(path: string, file: File) => {
    const body = new FormData()
    body.append('file', file)
    return request<T>(path, { method: 'POST', body })
  },
  /**
   * For viewing/downloading a file, not JSON. The access token lives only in
   * this module's own variable, never a cookie (see the file header), so a
   * plain `<a href>` to an API route would never carry it and would 401 --
   * fetch the bytes through here instead, then `URL.createObjectURL` the
   * result.
   */
  download: async (path: string): Promise<Blob> => {
    const headers: Record<string, string> = {}
    if (accessToken) headers['authorization'] = `Bearer ${accessToken}`
    const response = await fetch(`/api${path}`, { headers, credentials: 'same-origin' })
    if (!response.ok) throw new ApiError(response.status, 'ERROR', 'Could not open that file')
    return response.blob()
  },
  refresh: refreshOnce,
}
